/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type { ApiErrorDto, QuickstartDto } from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The "Get started" checklist: its items and which ones the workspace has done.
 */
export const getQuickstart = (id: string, signal?: AbortSignal) => {
  return customInstance<QuickstartDto>({
    url: `/api/v1/workspaces/${id}/quickstart`,
    method: 'GET',
    signal,
  });
};

export const getGetQuickstartQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/quickstart`] as const;
};

export const getGetQuickstartQueryOptions = <
  TData = Awaited<ReturnType<typeof getQuickstart>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getQuickstart>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetQuickstartQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getQuickstart>>> = ({
    signal,
  }) => getQuickstart(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getQuickstart>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetQuickstartQueryResult = NonNullable<
  Awaited<ReturnType<typeof getQuickstart>>
>;
export type GetQuickstartQueryError = ErrorType<ApiErrorDto>;

export function useGetQuickstart<
  TData = Awaited<ReturnType<typeof getQuickstart>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getQuickstart>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQuickstart>>,
          TError,
          Awaited<ReturnType<typeof getQuickstart>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQuickstart<
  TData = Awaited<ReturnType<typeof getQuickstart>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getQuickstart>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQuickstart>>,
          TError,
          Awaited<ReturnType<typeof getQuickstart>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQuickstart<
  TData = Awaited<ReturnType<typeof getQuickstart>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getQuickstart>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The "Get started" checklist: its items and which ones the workspace has done.
 */

export function useGetQuickstart<
  TData = Awaited<ReturnType<typeof getQuickstart>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getQuickstart>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetQuickstartQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Hide the checklist for you in this workspace. Safe to repeat.
 */
export const dismissQuickstart = (id: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/quickstart/dismiss`,
    method: 'POST',
    signal,
  });
};

export const getDismissQuickstartMutationKey = () => ['dismissQuickstart'] as const;

export const getDismissQuickstartMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof dismissQuickstart>>,
    TError,
    DismissQuickstartMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof dismissQuickstart>>,
  TError,
  DismissQuickstartMutationVariables,
  TContext
> => {
  const mutationKey = getDismissQuickstartMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof dismissQuickstart>>,
    DismissQuickstartMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return dismissQuickstart(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DismissQuickstartMutationResult = NonNullable<
  Awaited<ReturnType<typeof dismissQuickstart>>
>;

export type DismissQuickstartMutationError = ErrorType<ApiErrorDto>;
export type DismissQuickstartMutationVariables = { id: string };

/**
 * @summary Hide the checklist for you in this workspace. Safe to repeat.
 */
export const useDismissQuickstart = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof dismissQuickstart>>,
      TError,
      DismissQuickstartMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof dismissQuickstart>>,
  TError,
  DismissQuickstartMutationVariables,
  TContext
> => {
  return useMutation(getDismissQuickstartMutationOptions(options), queryClient);
};
/**
 * @summary Mark an item done. Only items without an automatic check can be marked.
 */
export const completeQuickstartItem = (id: string, key: string, signal?: AbortSignal) => {
  return customInstance<QuickstartDto>({
    url: `/api/v1/workspaces/${id}/quickstart/${key}/complete`,
    method: 'POST',
    signal,
  });
};

export const getCompleteQuickstartItemMutationKey = () =>
  ['completeQuickstartItem'] as const;

export const getCompleteQuickstartItemMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof completeQuickstartItem>>,
    TError,
    CompleteQuickstartItemMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof completeQuickstartItem>>,
  TError,
  CompleteQuickstartItemMutationVariables,
  TContext
> => {
  const mutationKey = getCompleteQuickstartItemMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof completeQuickstartItem>>,
    CompleteQuickstartItemMutationVariables
  > = (props) => {
    const { id, key } = props ?? {};

    return completeQuickstartItem(id, key);
  };

  return { mutationFn, ...mutationOptions };
};

export type CompleteQuickstartItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof completeQuickstartItem>>
>;

export type CompleteQuickstartItemMutationError = ErrorType<ApiErrorDto>;
export type CompleteQuickstartItemMutationVariables = { id: string; key: string };

/**
 * @summary Mark an item done. Only items without an automatic check can be marked.
 */
export const useCompleteQuickstartItem = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof completeQuickstartItem>>,
      TError,
      CompleteQuickstartItemMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof completeQuickstartItem>>,
  TError,
  CompleteQuickstartItemMutationVariables,
  TContext
> => {
  return useMutation(getCompleteQuickstartItemMutationOptions(options), queryClient);
};
